import React from 'react'
import { useTheme } from '../../contexts/ThemeContext'

/*
 * Chart palettes validated with the six-checks palette validator against the
 * actual card surfaces (#ffffff light, #020817 dark). Series hues are assigned
 * in this fixed order — never cycled past the end (fold to "Other" instead).
 */
export const chartPalettes = {
  light: {
    series: ['#2a78d6', '#008300', '#e87ba4', '#eda100', '#1baf7a', '#eb6834'],
    other: '#898781',
    revenue: '#2a78d6',
    cogs: '#e34948',
    profit: '#008300',
    grid: '#e1e0d9',
    axis: '#898781',
    ink: '#0b0b0b',
    inkSecondary: '#52514e',
    surface: '#ffffff',
  },
  dark: {
    series: ['#3987e5', '#008300', '#d55181', '#c98500', '#199e70', '#d95926'],
    other: '#898781',
    revenue: '#3987e5',
    cogs: '#e66767',
    profit: '#008300',
    grid: '#1e293b',
    axis: '#898781',
    ink: '#f8fafc',
    inkSecondary: '#cbd5e1',
    surface: '#020817',
  },
}

export const useChartTheme = () => {
  const { resolvedTheme } = useTheme()
  return chartPalettes[resolvedTheme === 'dark' ? 'dark' : 'light']
}

// Pick label ink for text sitting on a colored fill, by the fill's luminance
export const inkForFill = (hex) => {
  const n = parseInt(hex.slice(1), 16)
  const r = (n >> 16) & 255
  const g = (n >> 8) & 255
  const b = n & 255
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255
  return luminance > 0.6 ? '#0b0b0b' : '#ffffff'
}

// Compact figures for axis ticks and donut centers: 1.2M / 340K
export const compactNumber = (value) => {
  const abs = Math.abs(value)
  if (abs >= 1e9) return `${(value / 1e9).toFixed(1).replace(/\.0$/, '')}B`
  if (abs >= 1e6) return `${(value / 1e6).toFixed(1).replace(/\.0$/, '')}M`
  if (abs >= 1e3) return `${(value / 1e3).toFixed(1).replace(/\.0$/, '')}K`
  return `${Math.round(value)}`
}

// Themed recharts tooltip (pass as: <Tooltip content={<ChartTooltip formatter={…} />} />)
export const ChartTooltip = ({ active, payload, label, formatter }) => {
  if (!active || !payload || payload.length === 0) return null
  const format = formatter || ((v) => v)
  return (
    <div className="rounded-lg border border-border bg-popover px-3 py-2 text-xs shadow-md">
      {(label || payload[0]?.name) && (
        <p className="mb-1 font-medium text-popover-foreground">{label || payload[0]?.name}</p>
      )}
      <div className="space-y-1">
        {payload.map((entry, index) => (
          <div key={index} className="flex items-center gap-2">
            <span
              className="h-2.5 w-2.5 shrink-0 rounded-[3px]"
              style={{ backgroundColor: entry.color || entry.payload?.fill || entry.fill }}
            />
            <span className="text-muted-foreground">{entry.name}</span>
            <span className="ml-auto pl-3 font-medium tabular-nums text-popover-foreground">
              {format(entry.value)}
            </span>
          </div>
        ))}
      </div>
    </div>
  )
}

// Flat swatch legend; identity lives in the swatch, text stays in text tokens
export const ChartLegend = ({ items, formatter }) => {
  if (!items || items.length === 0) return null
  return (
    <div className="mt-3 flex flex-wrap justify-center gap-x-4 gap-y-1.5">
      {items.map((item) => (
        <div key={item.name} className="flex items-center gap-1.5 text-xs">
          <span
            className="h-2.5 w-2.5 shrink-0 rounded-[3px]"
            style={{ backgroundColor: item.color }}
          />
          <span className="text-muted-foreground">{item.name}</span>
          {item.value != null && formatter && (
            <span className="font-medium tabular-nums text-foreground">
              {formatter(item.value)}
            </span>
          )}
        </div>
      ))}
    </div>
  )
}

// Fold a name/value list to the top N slices plus a neutral "Other"
export const foldToOther = (data, maxSlices = 5) => {
  if (!data || data.length <= maxSlices) return data
  const sorted = [...data].sort((a, b) => b.value - a.value)
  const top = sorted.slice(0, maxSlices)
  const otherValue = sorted.slice(maxSlices).reduce((sum, d) => sum + d.value, 0)
  return [...top, { name: 'Other', value: otherValue, isOther: true }]
}
